import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, X } from 'lucide-react';
import classnames from 'classnames';
import CustomModal from '../components-library/CustomModal';
import { MODAL_SIZES, PROMO_CODE_DISCOUNT_TYPES } from '../services/exports/Constants';
import { GenericModalProps } from '../services/exports/Interfaces';
import useAppMethods from '../hooks/utility/useAppMethods';
import TextInput from '../components-library/inputs/TextInput';
import usePromoCode from '../hooks/order/usePromoCode';
import useGiftCard from '../features/gift-cards/hooks/useGiftCard';
import useAuth from '../hooks/global/useAuth';
import useGetPromoCodesApi from '../hooks/api/companies/useGetPromoCodesApi';
import { PromoCodeData } from '../store/api-slices';
import DiscountGeneralNote from '../features/discount-exclusivity/DiscountGeneralNote';
import DiscountComparisonChoice from '../features/discount-exclusivity/DiscountComparisonChoice';
import useActiveDiscount from '../features/discount-exclusivity/useActiveDiscount';
import useShowExclusivityNote from '../features/discount-exclusivity/useShowExclusivityNote';

const GIFT_CARD_REGEX = /^\d{16}$/;

const AddPromoOrGiftCardModal = (_: GenericModalProps) => {
  // UX point 7 (PROD-407): only show the exclusivity note when a discount is already applied that
  // adding a promo/gift would displace — with nothing applied there is no conflict, so no note.
  const showGeneralNote = useShowExclusivityNote('promo_or_gift');

  return (
    <CustomModal open position="center" zIndex={1300} size={MODAL_SIZES.SM}>
      <div className="p-md space-y-md">
        <Header />
        {/* Single info label — the grey general note covers the full rule (no separate ⓘ/⚠ note). */}
        {showGeneralNote && <DiscountGeneralNote />}
        <AvailableCodes />
        <ManualEntry />
      </div>
    </CustomModal>
  );
};

const Header = () => {
  const { toggleAddPromoOrGiftCardModal } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:AddPromoOrGiftCardModal.header' });

  return (
    <div className="flex items-center justify-between">
      <h3 className="text-base font-bold text-brand-text-primary">{t('title')}</h3>
      <button
        type="button"
        className="text-muted-foreground hover:text-brand-text-primary"
        onClick={() => toggleAddPromoOrGiftCardModal({ openModal: false })}
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

const AvailableCodes = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:AddPromoOrGiftCardModal' });
  const { data: promoCodes } = useGetPromoCodesApi();

  if (!promoCodes?.length) {
    return null;
  }

  return (
    <div className="space-y-xs">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {t('available_codes.title', { count: promoCodes.length })}
      </p>
      <div className="space-y-xs">
        {promoCodes.map((code) => (
          <CodeRow key={code.id} code={code} />
        ))}
      </div>
    </div>
  );
};

const CodeRow = ({ code }: { code: PromoCodeData }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:AddPromoOrGiftCardModal' });
  const { formatCurrency } = useAppMethods();
  const { appliedPromoCode, applyPromoCode, removePromoCode, isLoading } = usePromoCode();
  const { toggleLoginModal, toggleAddPromoOrGiftCardModal } = useAppMethods();
  const { isLoggedIn } = useAuth();
  const activeDiscount = useActiveDiscount();
  const [showChoice, setShowChoice] = useState(false);

  const isApplied = appliedPromoCode === code.code;

  const nextComponent = code.components?.find((component) => !component.is_used);

  // Applying this code displaces a different active discount (offer / gift / points). Its € saving
  // is known only for fixed-amount codes; percentage codes have no order-independent € figure.
  const candidateSaving =
    code.discount_type === PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT ? (nextComponent?.discount ?? null) : null;
  const conflicting = activeDiscount && activeDiscount.kind !== 'promo';

  const description = (() => {
    if (nextComponent?.discount == null) return null;

    if (code.discount_type === PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE) {
      return t('available_codes.percentage_off', { discount: nextComponent.discount });
    }

    return t('available_codes.amount_off', { amount: formatCurrency(nextComponent.discount) });
  })();

  const minThreshold = nextComponent?.usage_threshold;
  const condition =
    minThreshold > 0 ? t('available_codes.min_threshold', { value: formatCurrency(minThreshold) }) : null;

  // Actually apply the code (displacing whatever was active). The backend + FE state enforce
  // one-at-a-time, so applying is itself the "use this instead" action.
  const applyNow = async () => {
    setShowChoice(false);

    if (!isLoggedIn) {
      toggleAddPromoOrGiftCardModal({ openModal: false });
      toggleLoginModal({
        openModal: true,
        onCloseRedirect: (success) => success && applyPromoCode(code.code, true),
      });

      return;
    }

    const success = await applyPromoCode(code.code, true);
    if (success) {
      toggleAddPromoOrGiftCardModal({ openModal: false });
    }
  };

  const onToggle = async () => {
    if (isApplied) {
      removePromoCode();

      return;
    }

    // A different discount is already active: don't silently displace it — let the customer
    // compare and choose (Inc 4). We only intercept when logged in; the login flow keeps its
    // existing path and the choice would re-surface on the next tap.
    if (conflicting && isLoggedIn) {
      setShowChoice(true);

      return;
    }

    await applyNow();
  };

  return (
    <div className="space-y-xs">
      <div
        className={classnames('flex items-center gap-sm rounded-lg border-xs px-sm py-xs transition-colors', {
          'border-success/40 bg-success/5': isApplied,
          'border-brand-border bg-muted/30': !isApplied,
        })}
      >
        <Tag className={classnames('w-4 h-4 shrink-0', isApplied ? 'text-success' : 'text-brand-primary')} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-text-primary">{code.code}</p>
          {(description || condition) && (
            <p className="text-xs text-muted-foreground">
              {description}
              {description && condition && ' · '}
              {condition}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={isLoading}
          className={classnames('text-xs font-semibold px-sm py-[6px] rounded-lg transition-all shrink-0', {
            'bg-success/15 text-success': isApplied,
            'bg-brand-primary text-brand-button-primary-text hover:opacity-90': !isApplied,
            'opacity-50 cursor-not-allowed': isLoading,
          })}
        >
          {isApplied ? t('content.buttons.applied') : t('content.buttons.apply')}
        </button>
      </div>
      {showChoice && activeDiscount && conflicting && (
        <DiscountComparisonChoice
          active={activeDiscount}
          candidate={{ kind: 'promo', label: code.code, saving: candidateSaving }}
          onUseInstead={applyNow}
          onKeep={() => setShowChoice(false)}
        />
      )}
    </div>
  );
};

const ManualEntry = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:AddPromoOrGiftCardModal.content' });
  const [code, setCode] = useState('');
  const { isLoggedIn } = useAuth();
  const { toggleLoginModal, toggleAddPromoOrGiftCardModal } = useAppMethods();
  const { isLoading: isPromoLoading, applyPromoCode } = usePromoCode();
  const { apply, isLoading: isGiftCardLoading } = useGiftCard();

  const login = (): void => {
    toggleAddPromoOrGiftCardModal({ openModal: false });
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: (success) => success && _applyPromoCode(),
    });
  };

  const giftCardHandler = () => {
    apply(code).then((success) => {
      if (!success) {
        setCode('');

        return;
      }

      toggleAddPromoOrGiftCardModal({ openModal: false });
    });
  };

  const promoCodeHandler = () => {
    if (!isLoggedIn) {
      return login();
    }

    _applyPromoCode().then((success) => {
      if (!success) return;

      toggleAddPromoOrGiftCardModal({ openModal: false });
    });
  };

  const _applyPromoCode = () =>
    applyPromoCode(code, true).then((success) => {
      if (success) return success;

      setCode('');
    });

  const onApplyClick = () => {
    if (!code.length) return;

    const isGiftCard = GIFT_CARD_REGEX.test(code);
    isGiftCard ? giftCardHandler() : promoCodeHandler();
  };

  const isLoading = isPromoLoading || isGiftCardLoading;
  const canApply = code.trim().length > 0;

  return (
    <div className="border-t-xs border-brand-border pt-md">
      <div className="flex gap-xs">
        {/*
          `TextInput` spreads `...rest` onto its <input>, so `data-test` lands as a real attribute with
          no change to the component. (`CheckmarkTextInput` does NOT spread — that is why the customer
          -info fields are still driven through Redux instead of typed.)
        */}
        <TextInput
          autoFocus
          data-test="promo-or-gift-card-input"
          onChange={(value) => setCode(value?.toUpperCase().trim())}
          value={code}
          placeholder={t('input.placeholder')}
          inputContainerStyle="flex-1"
        />
        <button
          type="button"
          data-test="promo-or-gift-card-apply"
          disabled={!canApply || isLoading}
          onClick={onApplyClick}
          className={classnames(applyButtonStyle, {
            'opacity-50 cursor-not-allowed': !canApply || isLoading,
          })}
        >
          {t('buttons.apply')}
        </button>
      </div>
    </div>
  );
};

const applyButtonStyle =
  'px-md h-9 rounded-lg text-sm font-semibold bg-brand-primary text-brand-button-primary-text hover:opacity-90 transition-opacity shrink-0';

export default AddPromoOrGiftCardModal;
